import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { CommunityApi } from '../../services/api';
import { HUB_TILES, VALUE_PILLARS, QC_PATHS } from './qoreConnectConfig';
import { QcTileIcon, QcPillarIcon, QC_ICONS } from './QoreConnectIcons';
import { fmtTimeAgo } from './qoreConnectUtils';
import { resolveFeaturedMembers } from '../../data/founderMentor';

const STAT_COLORS = ['#22d3ee', '#a78bfa', '#34d399', '#10b981'];

const QoreConnectHub = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [discussions, setDiscussions] = useState([]);
  const [members, setMembers] = useState([]);
  const [activities, setActivities] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, discussionsRes, membersRes, activityRes] = await Promise.all([
        CommunityApi.getStats().catch(() => ({ data: null })),
        CommunityApi.getDiscussions({ limit: 6 }).catch(() => ({ data: [] })),
        CommunityApi.getFeaturedMembers().catch(() => ({ data: [] })),
        CommunityApi.getActivityFeed({ limit: 8 }).catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data || {
        totalMembers: 0,
        totalMentors: 0,
        activeDiscussions: 0,
      });
      setDiscussions(discussionsRes.data || []);
      setMembers(resolveFeaturedMembers(membersRes.data || []));
      setActivities(activityRes.data || []);
    } catch (err) {
      console.error('QoreConnect hub:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const statItems = [
    { key: 'members', value: stats?.totalMembers ?? 0, labelKey: 'qoreConnect.stats.members' },
    { key: 'mentors', value: stats?.totalMentors ?? 0, labelKey: 'qoreConnect.stats.mentors' },
    { key: 'discussions', value: stats?.activeDiscussions ?? 0, labelKey: 'qoreConnect.stats.discussions' },
    { key: 'spaces', value: 3, labelKey: 'qoreConnect.stats.spaces' },
  ];

  const Spark = QC_ICONS.spark;

  return (
    <>
      <header className="qc-hero">
        <div className="qc-hero__badge">{t('qoreConnect.hero.badge')}</div>
        <h1 className="qc-hero__title">
          <span className="qc-accent-cyan">{t('qoreConnect.hero.connect')}</span>{' '}
          <span className="qc-accent-violet">{t('qoreConnect.hero.learn')}</span>{' '}
          <span className="qc-accent-emerald">{t('qoreConnect.hero.grow')}</span>
        </h1>
        <p className="qc-hero__subtitle">{t('qoreConnect.hero.subtitle')}</p>
        <div className="qc-hero__stats" aria-live="polite">
          {statItems.map((s, i) => (
            <div key={s.key}>
              <div className="qc-hero__stat-val" style={{ color: STAT_COLORS[i] }}>
                {loading && s.key !== 'spaces' ? '—' : Number(s.value).toLocaleString()}
              </div>
              <div className="qc-hero__stat-label">{t(s.labelKey)}</div>
            </div>
          ))}
        </div>
      </header>

      <div className="qc-ai-strip" role="region" aria-labelledby="qc-ai-heading">
        <div className="qc-ai-strip__icon">
          <Spark />
        </div>
        <div className="qc-ai-strip__text">
          <h3 id="qc-ai-heading">{t('qoreConnect.ai.title')}</h3>
          <p>{t('qoreConnect.ai.desc')}</p>
        </div>
        <Link to={QC_PATHS.forum} className="qc-btn qc-btn--primary">
          {t('qoreConnect.ai.cta')}
        </Link>
      </div>

      <section aria-labelledby="qc-hub-heading">
        <div className="qc-section-head">
          <h2 id="qc-hub-heading">{t('qoreConnect.hub.title')}</h2>
          <p>{t('qoreConnect.hub.subtitle')}</p>
        </div>
        <div className="qc-tiles qc-tiles--three">
          {HUB_TILES.map((tile) => (
            <Link
              key={tile.id}
              to={tile.path}
              className="qc-tile qc-tile--featured"
              style={{ borderColor: `${tile.color}30` }}
            >
              <div className="qc-tile__top">
                <QcTileIcon name={tile.icon} color={tile.color} />
                <span className="qc-tile__arrow" aria-hidden>→</span>
              </div>
              <h3>{t(tile.labelKey)}</h3>
              <p>{t(tile.descKey)}</p>
            </Link>
          ))}
        </div>
      </section>

      <section aria-labelledby="qc-pillars-heading" className="qc-pillars-section">
        <div className="qc-section-head">
          <h2 id="qc-pillars-heading">{t('qoreConnect.pillars.sectionTitle')}</h2>
          <p>{t('qoreConnect.pillars.sectionDesc')}</p>
        </div>
        <div className="qc-pillars qc-pillars--three">
          {VALUE_PILLARS.map((p) => (
            <article key={p.id} className="qc-pillar">
              <QcPillarIcon name={p.icon} />
              <h4>{t(p.labelKey)}</h4>
              <p>{t(p.descKey)}</p>
            </article>
          ))}
        </div>
      </section>

      <div className="qc-layout">
        <div>
          <section className="qc-panel" aria-labelledby="qc-discussions-heading">
            <div className="qc-panel__head">
              <h3 id="qc-discussions-heading">{t('qoreConnect.feed.discussions')}</h3>
              <Link to={QC_PATHS.forum} className="qc-link">{t('qoreConnect.viewAll')}</Link>
            </div>
            <div className="qc-panel__body">
              {loading && [0, 1, 2].map((i) => (
                <div key={i} className="qc-skeleton" style={{ height: 56, margin: '0.5rem' }} />
              ))}
              {!loading && discussions.length === 0 && (
                <div className="qc-empty">
                  <p>{t('qoreConnect.feed.noDiscussions')}</p>
                  <Link to={QC_PATHS.forum} className="qc-btn qc-btn--sm qc-btn--primary" style={{ marginTop: '0.75rem' }}>
                    {t('qoreConnect.feed.startDiscussion')}
                  </Link>
                </div>
              )}
              {!loading && discussions.map((d, idx) => {
                const authorName = `${d.author?.firstName || ''} ${d.author?.lastName || ''}`.trim() || t('qoreConnect.anonymous');
                return (
                  <Link key={d._id || d.id || idx} to={QC_PATHS.forum} className="qc-feed-item">
                    <p className="qc-feed-item__title">{d.title}</p>
                    <div className="qc-feed-meta">
                      <span>{authorName}</span>
                      <span>{t('qoreConnect.feed.replies', { count: d.replies?.length || 0 })}</span>
                      <span>{fmtTimeAgo(d.createdAt, t)}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        </div>

        <div>
          <section className="qc-panel" aria-labelledby="qc-mentors-heading">
            <div className="qc-panel__head">
              <h3 id="qc-mentors-heading">{t('qoreConnect.feed.mentors')}</h3>
              <Link to={QC_PATHS.mentorship} className="qc-link">{t('qoreConnect.viewAll')}</Link>
            </div>
            <div className="qc-panel__body">
              {!loading && members.length === 0 && (
                <div className="qc-empty"><p>{t('qoreConnect.feed.noMentors')}</p></div>
              )}
              {!loading && members.slice(0, 5).map((m, idx) => {
                const profileUrl = m.profileUrl;
                const inner = (
                  <>
                    <p className="qc-feed-item__title">{m.name}</p>
                    <div className="qc-feed-meta">
                      <span>{m.title}{m.company ? ` · ${m.company}` : ''}</span>
                    </div>
                  </>
                );
                return profileUrl ? (
                  <a
                    key={m._id || m.id || idx}
                    href={profileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="qc-feed-item"
                  >
                    {inner}
                  </a>
                ) : (
                  <Link key={m._id || m.id || idx} to={QC_PATHS.mentorship} className="qc-feed-item">
                    {inner}
                  </Link>
                );
              })}
            </div>
          </section>

          <section className="qc-panel" style={{ marginTop: '1rem' }} aria-labelledby="qc-jobs-cta-heading">
            <div className="qc-panel__head">
              <h3 id="qc-jobs-cta-heading">{t('qoreConnect.feed.jobsTitle')}</h3>
              <Link to={QC_PATHS.jobs} className="qc-link">{t('qoreConnect.viewAll')}</Link>
            </div>
            <div className="qc-panel__body">
              <p className="qc-panel-blurb">{t('qoreConnect.feed.jobsDesc')}</p>
              <Link to={QC_PATHS.jobs} className="qc-btn qc-btn--primary" style={{ width: '100%', marginTop: '0.5rem' }}>
                {t('qoreConnect.feed.browseJobs')}
              </Link>
            </div>
          </section>

          <section className="qc-panel" style={{ marginTop: '1rem' }} aria-labelledby="qc-activity-heading">
            <div className="qc-panel__head">
              <h3 id="qc-activity-heading">{t('qoreConnect.feed.activity')}</h3>
            </div>
            <div className="qc-panel__body">
              {!loading && activities.length === 0 && (
                <div className="qc-empty"><p>{t('qoreConnect.feed.noActivity')}</p></div>
              )}
              {!loading && activities.map((act, idx) => (
                <div key={act._id || act.id || idx} className="qc-feed-item" style={{ cursor: 'default' }}>
                  <p className="qc-feed-item__title" style={{ fontWeight: 500, fontSize: '0.8rem' }}>
                    <strong>{act.user?.name}</strong> {act.action}
                    {act.target ? ` "${act.target}"` : ''}
                  </p>
                  <div className="qc-feed-meta">
                    <span>{fmtTimeAgo(act.timestamp, t)}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {!user && (
            <div className="qc-cta qc-cta--compact" style={{ marginTop: '1rem' }}>
              <h2>{t('qoreConnect.join.title')}</h2>
              <p>{t('qoreConnect.join.desc')}</p>
              <Link to="/register" className="qc-btn qc-btn--violet" style={{ width: '100%' }}>
                {t('qoreConnect.join.cta')}
              </Link>
            </div>
          )}
        </div>
      </div>

      <section className="qc-cta" aria-labelledby="qc-bottom-cta">
        <h2 id="qc-bottom-cta">{t('qoreConnect.cta.title')}</h2>
        <p>{t('qoreConnect.cta.desc')}</p>
        <div className="qc-cta__actions">
          <Link to={QC_PATHS.forum} className="qc-btn qc-btn--ghost">
            {t('qoreConnect.cta.joinForum')}
          </Link>
          <Link to={QC_PATHS.mentorship} className="qc-btn qc-btn--primary">
            {t('qoreConnect.cta.findMentor')}
          </Link>
          <Link to={QC_PATHS.jobs} className="qc-btn qc-btn--ghost">
            {t('qoreConnect.cta.exploreJobs')}
          </Link>
        </div>
      </section>
    </>
  );
};

export default QoreConnectHub;